import React from 'react';

import {
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { Link } from 'react-router-dom';
import { useGetOrganizationMembersQuery } from 'src/common/apis/AdminApi';
import { Pages } from 'src/common/constants/Pages';
import { theme } from 'src/common/theme/theme';
import { User, UserRoles } from 'src/common/types/User';
import { extractUserRole } from 'src/common/utils/User';

type Props = {
  organizationId: string;
};

const OrganizationMembersTable: React.FC<Props> = ({ organizationId }) => {
  const { data, isFetching } = useGetOrganizationMembersQuery({
    id: organizationId,
  });

  const displayUserRole = (user: User) => {
    const highestRole = extractUserRole(user);
    const role = extractUserRole(user, true);

    let string = '';

    if (highestRole === UserRoles.SUPER_ADMIN) string += 'Super Admin, ';

    // order matters
    if (role === UserRoles.USER) string += 'Member';
    if (role === UserRoles.CODER) string += 'Coder';
    if (role === UserRoles.ORG_OWNER) string += 'Owner';

    return (
      <Typography
        whiteSpace='normal'
        fontWeight={500}
        color={
          highestRole === UserRoles.SUPER_ADMIN
            ? theme.palette.error.main
            : 'black'
        }
        fontSize={16}
      >
        {string}
      </Typography>
    );
  };

  const renderCellContent = (text: any, userId: number) => {
    return (
      <Link
        className='unstyled-link full-width-block'
        style={{
          padding: '16px',
        }}
        to={Pages.getRoute(Pages.user, userId)}
      >
        {text}
      </Link>
    );
  };

  return (
    <Box
      sx={{
        paddingTop: '40px',
        paddingBottom: '56px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          marginBottom: '24px',
        }}
        flex={1}
      >
        <Typography variant='h6' fontSize={18} fontWeight={700}>
          Users
        </Typography>

        {isFetching && (
          <CircularProgress
            color='primary'
            size='24px'
            sx={{ marginLeft: '24px' }}
          />
        )}
      </Box>

      {!isFetching && (
        <Table>
          <TableHead
            sx={{
              backgroundColor: (theme) => theme.palette.primaryLight?.main,
              height: 52,
            }}
          >
            <TableRow>
              <TableCell>
                <Typography color='secondary'>Full Name</Typography>
              </TableCell>
              <TableCell>
                <Typography color='secondary'>Email</Typography>
              </TableCell>
              <TableCell>
                <Typography color='secondary'>Role</Typography>
              </TableCell>
              <TableCell>
                <Typography color='secondary'>Note Scans</Typography>
              </TableCell>
              <TableCell>
                <Typography color='secondary'>Keyword Searches</Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.map((user) => (
              <TableRow
                key={user.id}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                className={'admin-table-row'}
              >
                <TableCell component='th' scope='row' sx={{ padding: 0 }}>
                  {renderCellContent(user.fullName, user.id)}
                </TableCell>
                <TableCell sx={{ padding: 0 }}>
                  {renderCellContent(user.email, user.id)}
                </TableCell>
                <TableCell sx={{ padding: 0 }}>
                  {renderCellContent(displayUserRole(user), user.id)}
                </TableCell>
                <TableCell sx={{ padding: 0 }}>
                  {renderCellContent(user.scanSearchesUsed ?? 0, user.id)}
                </TableCell>
                <TableCell sx={{ padding: 0 }}>
                  {renderCellContent(user.keywordSearchesUsed ?? 0, user.id)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};

export default OrganizationMembersTable;
